import { useRef, useState, type ChangeEvent } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAdminGerarPresignedUploadMutation } from '../../api';

interface Props {
  /** URL atual (pra preview e pra "remover" zerando o valor). */
  valor: string | null | undefined;
  /** Disparado com a URL pública final após upload, ou `null` se removido. */
  onChange: (urlFinal: string | null) => void;
  /** Pasta lógica dentro do bucket (`logo`, `capa`, etc). */
  finalidade: 'logo' | 'capa' | 'produto' | 'avatar' | 'outros';
  /** Rótulo do input — ex.: "Logo da loja". Sem ele, o título acima da prévia não aparece. */
  rotulo?: string
  /** Variant do preview: `avatar` (quadrado), `capa` (retângulo wide), `retrato` (vertical 9:16) ou `banner` (2:1). */
  variant?: 'avatar' | 'capa' | 'retrato' | 'banner'
  /** `false` esconde a lixeira: quem usa já tem o próprio botão de remover o item inteiro. */
  removivel?: boolean
}

const ACEITOS = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';
const TAMANHO_MAX_MB = 5;

/** Prévia retangular: capa wide, retrato 9:16 (totem) e banner 2:1 (topo do cardápio, na largura toda até 320px). */
const TAMANHO_PREVIA = {
  capa: { width: 160, height: 90 },
  retrato: { width: 90, height: 160 },
  banner: { width: '100%', maxWidth: 320, aspectRatio: '2 / 1' },
} as const

/**
 * Upload de imagem com presigned URL. Fluxo:
 *  1. POST `/admin/storage/presigned-upload` → backend devolve `uploadUrl`
 *  2. PUT direto pro S3 (bytes não passam pelo Node)
 *  3. `onChange(urlFinal)` — a URL pública S3 é o que o pai persiste
 */
export function UploadImagem({ valor, onChange, finalidade, rotulo, variant = 'avatar', removivel = true }: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [gerar] = useAdminGerarPresignedUploadMutation();
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aoEscolherArquivo(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    setErro(null);
    const arquivo = e.target.files?.[0];
    e.target.value = ''; // reseta pra permitir re-selecionar o mesmo arquivo
    if (!arquivo) return;

    if (!arquivo.type.startsWith('image/')) {
      setErro('Selecione um arquivo de imagem.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX_MB * 1024 * 1024) {
      setErro(`Imagem muito grande — máximo ${TAMANHO_MAX_MB}MB.`);
      return;
    }

    setEnviando(true);
    try {
      const { uploadUrl, urlFinal } = await gerar({
        fileName: arquivo.name,
        fileType: arquivo.type,
        finalidade,
      }).unwrap();

      // PUT direto pro S3 — fora do RTK Query (URL externa).
      const resp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': arquivo.type },
        body: arquivo,
      });
      if (!resp.ok) {
        throw new Error(`Falha no upload (HTTP ${resp.status})`);
      }
      onChange(urlFinal);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao enviar imagem';
      setErro(msg);
    } finally {
      setEnviando(false);
    }
  }

  const preview = variant !== 'avatar' ? (
    <Box
      sx={{
        ...TAMANHO_PREVIA[variant],
        borderRadius: 1,
        bgcolor: 'action.hover',
        // Aspas no url(): nomes de arquivo com espaço/parêntese quebram sem elas.
        backgroundImage: valor ? `url("${valor}")` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        flexShrink: 0,
      }}
    />
  ) : (
    <Avatar
      src={valor ?? undefined}
      variant="rounded"
      sx={{ width: 80, height: 80, bgcolor: 'action.hover', flexShrink: 0 }}
    />
  );

  // Banner: a prévia ganha a largura toda e o botão desce para baixo dela, com a mesma largura.
  const emColuna = variant === 'banner'

  return (
    <Box sx={emColuna ? { width: '100%', maxWidth: 320 } : undefined}>
      {!!rotulo && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {rotulo}
        </Typography>
      )}
      <Stack
        direction={emColuna ? 'column' : { xs: 'column', sm: 'row' }}
        spacing={emColuna ? 1 : 2}
        alignItems={emColuna ? 'stretch' : { xs: 'flex-start', sm: 'center' }}
      >
        {preview}
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            startIcon={enviando ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={enviando}
            fullWidth={emColuna}
          >
            {valor ? 'Trocar imagem' : 'Enviar imagem'}
          </Button>
          {valor && !enviando && removivel && (
            <Tooltip title="Remover imagem">
              <IconButton color="error" onClick={() => onChange(null)} aria-label="Remover imagem">
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <input
          ref={inputRef}
          type="file"
          accept={ACEITOS}
          onChange={aoEscolherArquivo}
          style={{ display: 'none' }}
        />
      </Stack>
      {erro && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {erro}
        </Alert>
      )}
    </Box>
  );
}
